import { protect } from '../../function';

type FullTagDescription<TagType> = {
    type: TagType;
    id?: number | string;
};
type TagDescription<TagType> = TagType | FullTagDescription<TagType>;

/*
 * Класс для создания тэгов инвалидации по запросу в гридах
 *
 * Нужен для инвалидации всех запросов грида кроме отображаемого
 *
 */
export class InvalidateGridApiHelper<Tag extends string, Arg> {
    /**
     * Все зарегистрированные хэши запросов для грида
     * @private
     */
    private _hashes = new Set<string>();
    /**
     * Флаг для инвалидации всех запросов
     * @private
     */
    private _forceInvalidate = false;
    /**
     * Хеш последнего запроса (отображаемого) в гриде
     * @private
     */
    private _lastHash: string | null = null;

    /**
     * Аргументы последнего запроса в гриде
     * @private
     */
    private _lastArgs: Arg | null = null;

    /**
     * Возвращает аргументы последнего запроса в гриде
     * для обновления кеша в апи
     */
    get lastArgs() {
        return this._lastArgs;
    }

    constructor(
        private _tag: Tag,
        private _name: string
    ) {}

    /**
     * Создаем и сохраняем тэг для ручки грида
     */
    provideHashTagForGridEndpoint(args: Arg): TagDescription<Tag>[] {
        try {
            const hash = JSON.stringify(args);

            this._hashes.add(hash);

            return [{ type: this._tag, id: `${this._name}_${hash}` }];
        } catch {
            return [];
        }
    }

    /**
     * Метод возвращает тэги инвалидации запросов если требуется
     * При обновлении текущего запроса в гриде при переходе на новый запрос грида следует почистить тот что был показан
     */
    invalidateHashTagForGridEndpoint(): TagDescription<Tag>[] {
        if (!this._forceInvalidate) {
            return [];
        }

        this._forceInvalidate = false;

        const tags: TagDescription<Tag>[] = [];

        this._hashes.forEach((hash) => {
            tags.push({ type: this._tag, id: `${this._name}_${hash}` });
        });
        this._hashes.clear();

        return tags;
    }

    /**
     * Устанавливает параметры отображаемые в гриде
     * @param args - аргументы запроса
     */
    setCurrentGridRequest(args: Arg) {
        protect(() => {
            const hash = JSON.stringify(args);

            this._lastArgs = args;
            this._lastHash = hash;
        });
    }

    /**
     * Очищает параметры при скрытии грида
     */
    clearCurrentGridRequest() {
        this._lastHash = null;
        this._lastArgs = null;
    }

    getTagsToInvalidate(): Array<TagDescription<Tag>>;
    getTagsToInvalidate<TagType extends string>(
        additionalTags: Array<TagDescription<TagType>>
    ): Array<TagDescription<Tag | TagType>>;
    /**
     * Возвращает все сохраненные раннее тэги инвалидации для запроса кроме отображаемого гридом
     * @param additionalTags - массив дополнительных тэгов для конкатенации с возвращаемыми
     */
    getTagsToInvalidate<TagType extends string>(
        additionalTags?: Array<TagDescription<TagType>>
    ): Array<TagDescription<TagType | Tag>> {
        const invalidateTags: Array<TagDescription<TagType | Tag>> = additionalTags ? [...additionalTags] : [];

        this._hashes.forEach((savedHash) => {
            if (savedHash !== this._lastHash) {
                invalidateTags.push({ type: this._tag, id: `${this._name}_${savedHash}` });
            }
        });
        this._hashes = new Set(this._lastHash ? [this._lastHash] : []);
        this._forceInvalidate = true;

        return invalidateTags;
    }
}
